import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardBody, Container } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";

import TabPanel from "components/TabPanel";
import TabView from "components/TabView";
import Dados from "./Dados";
import Componentes from "./Componentes";

import TitleFormWithEdit from "components/TitleFormWithEdit";
import { showLoading, hideLoading } from "components/Loading";
import { erroInesperado } from "services/Utils";
import { toast } from "react-toastify";
import componenteService from "services/ComponenteService";
import { statusComponenteText, StatusComponenteEnum, EstadoComponenteEnum, estadoComponenteText } from "services/ComponenteService";
import { validate } from "components/validateGeneralForm";
import AppContext from "AppContext";
import comboService from "services/ComboService";

export default function FormComponente() {
  const { state } = useContext(AppContext)
  const [data, setData] = useState({})
  const [edit, setEdit] = useState(false)
  const [tiposComponente, setTiposComponente] = useState([])
  const [modelosComponente, setModelosComponente] = useState([])
  const params = useParams()
  const formRef = useRef(null)
  const navigate = useNavigate()

  const id = parseInt(params.id)
  const novo = (0 === id)

  const podeGerenciar = (state?.user?.acoes || []).includes('componente.gerenciar')

  const abaComponentesDisabled = novo || !!data.pai

  const estadoOptions = useMemo(() => {
    return Object.values(EstadoComponenteEnum).map(v => ({ value: v, label: estadoComponenteText(v) }))
  }, [])

  const carregar = () => {
    componenteService.getById(id).then(res => {
      if (res.success) {
        res.data.statusText = statusComponenteText(res.data.status)
        setData(res.data)
      }
    })
  }

  useEffect(() => {
    comboService.tipoComponente().then(res => {
      if (res.success) {
        setTiposComponente(res.data)
      }
    })
  }, [])

  useEffect(() => {
    if (!!id) {
      carregar()
    } else {
      setData({ idContrato: state?.contrato?.id })
    }
  }, [id])

  useEffect(() => {
    if (data.idTipoComponente) {
      comboService.modeloComponente(data.idTipoComponente).then(res => {
        if (res.success) {
          setModelosComponente(res.data)
        }
      })
    } else {
      setModelosComponente([])
    }
  }, [data.idTipoComponente])

  useEffect(() => {
    if (novo) {
      setEdit(true)
    }
  }, [novo])

  const handleChangeEdit = (value) => {
    if (value && !novo && data?.status != StatusComponenteEnum.ESTOQUE) {
      toast('Não é possível alterar um componente que não está no status ESTOQUE')
      setEdit(false)
      return
    }

    setEdit(value)
  }

  const formConfig = useMemo(() => {
    if (novo) {
      return [
        [
          {
            name: "idTipoComponente", label: 'Tipo Componente', type: "combo", required: true, colspan: 4,
            options: tiposComponente, optionValue: 'id', optionLabel: 'descricao', showDefault: true,
          },
          {
            name: "idModeloComponente", label: 'Modelo', type: "combo", required: true, colspan: 4,
            options: modelosComponente, optionValue: 'id', optionLabel: 'descricao', showDefault: true,
            disabled: !data.idTipoComponente,
          },
          {
            name: "estado", label: 'Estado de Conservação', type: "combo", required: true, colspan: 4,
            options: estadoOptions, showDefault: true,
          },
          { name: "qrCode", label: 'QR Code', type: "text", required: true, colspan: 4 },
          { name: "quantidadeComponentes", label: <>Quantidade de componentes <small><i>- criação sequencial, deixe vazio para criar apenas 1</i></small></>, type: "integer", required: false, colspan: 5 },
        ],
      ]
    }

    return [
      [
        { name: "tipoComponente", label: 'Tipo', type: "text", required: true, colspan: 4, disabled: true },
        {
          name: "idModeloComponente", label: 'Modelo', type: "combo", colspan: 4, disabled: !edit,
          options: modelosComponente, optionValue: 'id', optionLabel: 'descricao',
        },
        {
          name: "estado", label: 'Estado de Conservação', type: "combo", colspan: 4, disabled: !edit,
          options: estadoOptions,
        },
        { name: "qrCode", label: 'QR Code', type: "text", required: true, colspan: 4, disabled: true },
        { name: "statusText", label: 'Status', type: "text", required: true, colspan: 4, disabled: true },
      ],
    ]
  }, [novo, tiposComponente, modelosComponente, estadoOptions, edit, data.idTipoComponente])

  const handleChange = (field, value) => {
    if (field === 'idTipoComponente') {
      setData(data => ({ ...data, idTipoComponente: value, idModeloComponente: null }))
      return
    }
    setData(data => ({ ...data, [field]: value }))
  }

  const enviar = async () => {
    const errors = validate(formConfig, data)
    if (errors.length > 0) {
      setData(data => ({ ...data, errors }))
      return
    }

    try {
      showLoading()
      if (novo) {
        await componenteService.create(data.idContrato, data.idTipoComponente, data.qrCode, data.idModeloComponente, data.estado, data.quantidadeComponentes)
      } else {
        await componenteService.update(id, data.idModeloComponente, data.estado)
      }
      toast('Registro salvo com sucesso!')
      hideLoading()
      navigate('/admin/componentes')
    } catch (ex) {
      hideLoading()
      console.error(ex)
      erroInesperado()
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    await enviar()
  }

  return (
    <Container fluid>
      <TitleFormWithEdit title="Componentes" edit={edit} onChangeEdit={handleChangeEdit} showButton={!novo && podeGerenciar} />
      <Card>
        <CardBody>
          <TabView defaultTab={1}>
            <TabPanel label="Dados">
              {novo || edit ? (
                <form onSubmit={handleSubmit} ref={formRef}>
                  <Dados data={data} onChange={handleChange} formConfig={formConfig} edit={edit} novo={novo} />
                  <div className="text-right">
                    <Button type="submit" color="primary">Salvar</Button>
                  </div>
                </form>
              ) : (
                <Dados data={data} onChange={handleChange} formConfig={formConfig} edit={false} novo={false} />
              )}
            </TabPanel>
            <TabPanel label="Componentes" disabled={abaComponentesDisabled}>
              <Componentes pai={data} onChange={carregar} podeGerenciar={podeGerenciar} />
            </TabPanel>
          </TabView>
        </CardBody>
      </Card>
    </Container>
  )
}
